import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { 
  Users, 
  Plus, 
  Search, 
  Filter, 
  Edit3, 
  Trash2, 
  X, 
  CheckCircle2, 
  ShieldCheck, 
  Mail, 
  Phone, 
  Globe2, 
  Briefcase, 
  Languages, 
  Calendar,
  Layers,
  Sparkles,
  BookOpen
} from 'lucide-react';
import { StaffMember, StaffDepartment, UserRole } from '@ftt/shared';
import { STAFF_DEPARTMENTS_CONFIG, INDIAN_LANGUAGES_LIST } from '@ftt/shared';

export const AdminStaffPage: React.FC = () => {
  const [selectedDept, setSelectedDept] = useState<string>('ALL');
  const [selectedRole, setSelectedRole] = useState<string>('ALL');
  const [searchQuery, setSearchQuery] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingStaff, setEditingStaff] = useState<StaffMember | null>(null);

  // Form State
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [jobTitle, setJobTitle] = useState('');
  const [department, setDepartment] = useState<StaffDepartment>('EDITORIAL');
  const [role, setRole] = useState<UserRole>('EDITOR');
  const [bio, setBio] = useState('');
  const [assignedLanguages, setAssignedLanguages] = useState<string[]>(['English']);
  const [assignedProjectCount, setAssignedProjectCount] = useState(0);
  const [isActive, setIsActive] = useState(true);
  const [joinedAt, setJoinedAt] = useState('');

  const queryClient = useQueryClient();

  const { data: staffRes, isLoading } = useQuery({
    queryKey: ['admin-staff', { dept: selectedDept, role: selectedRole, search: searchQuery }],
    queryFn: () => {
      let url = '/admin/staff';
      const params = new URLSearchParams();
      if (selectedDept !== 'ALL') params.append('department', selectedDept);
      if (selectedRole !== 'ALL') params.append('role', selectedRole);
      if (searchQuery) params.append('search', searchQuery);
      if (params.toString()) url += `?${params.toString()}`;
      return api.get(url).then((res) => res.data.data);
    },
  });

  const staffList: StaffMember[] = staffRes || [];

  const saveMutation = useMutation({
    mutationFn: async () => {
      const payload = {
        name,
        email,
        phone: phone || null,
        jobTitle,
        department,
        role,
        bio: bio || null,
        assignedLanguages,
        assignedProjectCount,
        isActive,
        joinedAt: joinedAt || new Date().toISOString(),
      };

      if (editingStaff?.id) {
        return api.put(`/admin/staff/${editingStaff.id}`, payload);
      } else {
        return api.post('/admin/staff', payload);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-staff'] });
      setIsModalOpen(false);
      resetForm();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/admin/staff/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-staff'] });
    },
  });

  const resetForm = () => {
    setEditingStaff(null);
    setName('');
    setEmail('');
    setPhone('');
    setJobTitle('');
    setDepartment('EDITORIAL');
    setRole('EDITOR');
    setBio('');
    setAssignedLanguages(['English']);
    setAssignedProjectCount(0);
    setIsActive(true);
    setJoinedAt(new Date().toISOString().split('T')[0]);
  };

  const handleEdit = (staff: StaffMember) => {
    setEditingStaff(staff);
    setName(staff.name);
    setEmail(staff.email);
    setPhone(staff.phone || '');
    setJobTitle(staff.jobTitle);
    setDepartment(staff.department);
    setRole(staff.role);
    setBio(staff.bio || '');
    setAssignedLanguages(staff.assignedLanguages || ['English']);
    setAssignedProjectCount(staff.assignedProjectCount || 0);
    setIsActive(staff.isActive);
    setJoinedAt(staff.joinedAt ? staff.joinedAt.split('T')[0] : '');
    setIsModalOpen(true);
  };

  const handleCreate = () => {
    resetForm();
    setIsModalOpen(true);
  };

  const toggleLanguage = (lang: string) => {
    if (assignedLanguages.includes(lang)) {
      setAssignedLanguages(assignedLanguages.filter((l) => l !== lang));
    } else {
      setAssignedLanguages([...assignedLanguages, lang]);
    }
  };

  // Summary Metrics
  const totalStaff = staffList.length;
  const activeCount = staffList.filter((s) => s.isActive).length;
  const translationStaffCount = staffList.filter((s) => s.department === 'TRANSLATION').length;
  const allLanguages = Array.from(new Set(staffList.flatMap((s) => s.assignedLanguages || [])));

  return (
    <div className="space-y-6 font-sans text-slate-900 antialiased">
      
      {/* 1. Header & Primary Action */}
      <div className="bg-white rounded-2xl md:rounded-3xl p-6 shadow-sm border border-slate-200/90 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <span className="text-[10px] text-brand-700 font-mono uppercase tracking-widest font-bold">
              ORGANIZATION &amp; TALENT
            </span>
            <span className="text-slate-400 font-mono text-xs">&bull;</span>
            <span className="text-xs text-slate-600 font-serif italic">
              FOR THE TRUTH PRESS PRIVATE LIMITED
            </span>
          </div>
          <h1 className="text-2xl sm:text-3xl font-serif font-black text-slate-900 flex items-center gap-2.5 mt-1">
            <Users className="w-7 h-7 text-[#FF6B4A]" />
            <span>Internal Staff &amp; Role Directory</span>
          </h1>
          <p className="text-xs sm:text-sm text-slate-600 font-medium mt-1">
            Manage editorial leads, theological translators, production directors, and operational staff permissions.
          </p>
        </div>

        <button
          onClick={handleCreate}
          className="px-5 py-2.5 rounded-xl bg-slate-900 hover:bg-slate-800 text-white font-extrabold text-xs shadow-md transition-all flex items-center gap-2 shrink-0 cursor-pointer"
        >
          <Plus className="w-4 h-4 text-[#FF6B4A]" />
          <span>Add Staff Member</span>
        </button>
      </div>

      {/* 2. KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="p-5 rounded-2xl bg-white border border-slate-200/90 shadow-sm space-y-1">
          <div className="flex items-center justify-between">
            <span className="text-xs text-slate-600 font-bold font-mono uppercase tracking-wider">Total Team Members</span>
            <Users className="w-4 h-4 text-brand-600" />
          </div>
          <div className="text-2xl font-black font-serif text-slate-900">{totalStaff}</div>
          <div className="text-[11px] text-emerald-700 font-bold font-mono">{activeCount} Currently Active</div>
        </div>

        <div className="p-5 rounded-2xl bg-white border border-slate-200/90 shadow-sm space-y-1">
          <div className="flex items-center justify-between">
            <span className="text-xs text-slate-600 font-bold font-mono uppercase tracking-wider">Translation Department</span>
            <Languages className="w-4 h-4 text-purple-600" />
          </div>
          <div className="text-2xl font-black font-serif text-slate-900">{translationStaffCount}</div>
          <div className="text-[11px] text-purple-700 font-bold font-mono">Vernacular Linguists</div>
        </div>

        <div className="p-5 rounded-2xl bg-white border border-slate-200/90 shadow-sm space-y-1">
          <div className="flex items-center justify-between">
            <span className="text-xs text-slate-600 font-bold font-mono uppercase tracking-wider">Languages Covered</span>
            <Globe2 className="w-4 h-4 text-emerald-600" />
          </div>
          <div className="text-2xl font-black font-serif text-slate-900">{allLanguages.length}</div>
          <div className="text-[11px] text-slate-600 font-bold font-mono">Indian Vernaculars</div>
        </div>

        <div className="p-5 rounded-2xl bg-white border border-slate-200/90 shadow-sm space-y-1">
          <div className="flex items-center justify-between">
            <span className="text-xs text-slate-600 font-bold font-mono uppercase tracking-wider">Access Roles</span>
            <ShieldCheck className="w-4 h-4 text-amber-600" />
          </div>
          <div className="text-2xl font-black font-serif text-slate-900">3 Tiers</div>
          <div className="text-[11px] text-amber-700 font-bold font-mono">Super Admin / Admin / Editor</div>
        </div>
      </div>

      {/* 3. Filter Toolbar */}
      <div className="p-4 rounded-2xl bg-white border border-slate-200/90 shadow-sm flex flex-col md:flex-row items-center justify-between gap-4">
        <div className="relative w-full md:w-80">
          <Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            placeholder="Search by name, email, role, language..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 rounded-xl bg-slate-50 border border-slate-200 text-xs text-slate-900 placeholder-slate-400 focus:outline-hidden focus:ring-2 focus:ring-brand-500"
          />
        </div>

        <div className="flex flex-wrap items-center gap-2 w-full md:w-auto">
          {/* Department Filter */}
          <select
            value={selectedDept}
            onChange={(e) => setSelectedDept(e.target.value)}
            className="px-3 py-2 rounded-xl bg-slate-50 border border-slate-200 text-xs text-slate-800 font-bold focus:outline-hidden cursor-pointer"
          >
            <option value="ALL">All Departments</option>
            {Object.entries(STAFF_DEPARTMENTS_CONFIG).map(([key, config]) => (
              <option key={key} value={key}>{config.label}</option>
            ))}
          </select>

          {/* Role Filter */}
          <select
            value={selectedRole}
            onChange={(e) => setSelectedRole(e.target.value)}
            className="px-3 py-2 rounded-xl bg-slate-50 border border-slate-200 text-xs text-slate-800 font-bold focus:outline-hidden cursor-pointer"
          >
            <option value="ALL">All Roles</option>
            <option value="SUPER_ADMIN">Super Admin</option>
            <option value="ADMIN">Admin</option>
            <option value="EDITOR">Editor</option>
          </select>
        </div>
      </div>

      {/* 4. Staff Members Grid */}
      {isLoading ? (
        <div className="p-16 text-center text-xs font-mono text-slate-500 bg-white rounded-2xl border border-slate-200">Loading staff records...</div>
      ) : staffList.length === 0 ? (
        <div className="p-16 rounded-2xl bg-white border border-slate-200 text-center space-y-3 shadow-sm">
          <Users className="w-12 h-12 text-slate-400 mx-auto" />
          <div className="text-base font-bold text-slate-900">No Staff Members Found</div>
          <p className="text-xs text-slate-500 max-w-sm mx-auto">
            Try adjusting your search criteria or add a new staff member to the internal directory.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {staffList.map((staff) => {
            const deptLabel = STAFF_DEPARTMENTS_CONFIG[staff.department]?.label || staff.department;
            const deptColors: Record<StaffDepartment, string> = {
              MANAGEMENT: 'bg-amber-100 text-amber-900 border border-amber-300',
              EDITORIAL: 'bg-blue-100 text-blue-900 border border-blue-300',
              TRANSLATION: 'bg-purple-100 text-purple-900 border border-purple-300',
              PRODUCTION_DESIGN: 'bg-indigo-100 text-indigo-900 border border-indigo-300',
              FINANCE_OPERATIONS: 'bg-emerald-100 text-emerald-900 border border-emerald-300',
              PARTNER_RELATIONS: 'bg-rose-100 text-rose-900 border border-rose-300',
            };
            const deptColor = deptColors[staff.department] || 'bg-slate-100 text-slate-800';

            return (
              <div
                key={staff.id}
                className="p-6 rounded-2xl md:rounded-3xl bg-white border border-slate-200/90 hover:border-brand-300 hover:shadow-md transition-all flex flex-col justify-between space-y-4 shadow-sm"
              >
                <div className="space-y-3">
                  {/* Top Bar: Dept Badge & Status */}
                  <div className="flex items-center justify-between">
                    <span className={`px-2.5 py-1 rounded-lg text-[10px] font-mono font-bold uppercase tracking-wider ${deptColor}`}>
                      {deptLabel}
                    </span>
                    <div className="flex items-center gap-1.5">
                      <span className={`w-2 h-2 rounded-full ${staff.isActive ? 'bg-emerald-500' : 'bg-slate-400'}`} />
                      <span className="text-[10px] font-mono uppercase font-bold text-slate-600">
                        {staff.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                  </div>

                  {/* Profile Info */}
                  <div className="flex items-start gap-3 pt-1">
                    <div className="w-12 h-12 rounded-2xl bg-slate-900 text-white flex items-center justify-center font-black text-base shrink-0 shadow-xs">
                      {staff.name.split(' ').map((n) => n[0]).join('').slice(0, 2)}
                    </div>
                    <div className="overflow-hidden">
                      <h3 className="font-serif font-black text-base text-slate-900 truncate">
                        {staff.name}
                      </h3>
                      <div className="text-xs text-brand-700 font-bold truncate">
                        {staff.jobTitle}
                      </div>
                      <div className="flex items-center gap-2 pt-1 text-[10px] font-mono">
                        <span className={`px-2 py-0.5 rounded-md font-bold ${
                          staff.role === 'SUPER_ADMIN' ? 'bg-amber-100 text-amber-900 border border-amber-300' :
                          staff.role === 'ADMIN' ? 'bg-blue-100 text-blue-900 border border-blue-300' :
                          'bg-slate-100 text-slate-800 border border-slate-200'
                        }`}>
                          {staff.role}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Bio */}
                  {staff.bio && (
                    <p className="text-xs text-slate-600 leading-relaxed font-serif italic line-clamp-2">
                      &ldquo;{staff.bio}&rdquo;
                    </p>
                  )}

                  {/* Languages Covered */}
                  {staff.assignedLanguages && staff.assignedLanguages.length > 0 && (
                    <div className="space-y-1.5 pt-2 border-t border-slate-100">
                      <span className="text-[10px] font-mono font-bold uppercase text-slate-500 block">Languages &amp; Vernaculars</span>
                      <div className="flex flex-wrap gap-1">
                        {staff.assignedLanguages.map((lang) => (
                          <span
                            key={lang}
                            className="px-2 py-0.5 rounded-md bg-slate-50 text-slate-700 text-[10px] font-mono font-bold border border-slate-200"
                          >
                            {lang}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Contact Info */}
                  <div className="space-y-1 text-xs text-slate-600 pt-2 border-t border-slate-100 font-mono font-semibold">
                    <div className="flex items-center gap-2 truncate">
                      <Mail className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                      <span className="truncate">{staff.email}</span>
                    </div>
                    {staff.phone && (
                      <div className="flex items-center gap-2">
                        <Phone className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                        <span>{staff.phone}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Footer Actions */}
                <div className="pt-3 border-t border-slate-100 flex items-center justify-between text-xs">
                  <span className="text-[10px] text-slate-600 font-mono font-bold">
                    Projects: <strong className="text-slate-900">{staff.assignedProjectCount || 0}</strong>
                  </span>

                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => handleEdit(staff)}
                      className="p-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 hover:text-slate-900 transition-colors cursor-pointer"
                      title="Edit Staff Member"
                    >
                      <Edit3 className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => {
                        if (confirm(`Remove staff member ${staff.name}?`)) {
                          deleteMutation.mutate(staff.id);
                        }
                      }}
                      className="p-1.5 rounded-lg bg-slate-100 hover:bg-rose-100 text-slate-600 hover:text-rose-700 transition-colors cursor-pointer"
                      title="Delete Staff Member"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Modal for Creating / Editing Staff */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl max-w-xl w-full p-6 sm:p-8 space-y-6 shadow-2xl border border-slate-200 text-slate-900">
            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
              <h3 className="font-serif text-xl font-bold text-slate-900">
                {editingStaff ? 'Edit Staff Member' : 'Add New Staff Member'}
              </h3>
              <button onClick={() => setIsModalOpen(false)} className="text-slate-400 hover:text-slate-900 cursor-pointer">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4 text-xs">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block font-bold text-slate-700 uppercase font-mono mb-1">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="e.g. Samuel Rajan"
                    className="w-full px-3 py-2 rounded-xl bg-slate-50 border border-slate-200 text-slate-900 font-semibold focus:outline-hidden"
                  />
                </div>

                <div>
                  <label className="block font-bold text-slate-700 uppercase font-mono mb-1">
                    Corporate Email *
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="name@forthetruth.in"
                    className="w-full px-3 py-2 rounded-xl bg-slate-50 border border-slate-200 text-slate-900 font-semibold focus:outline-hidden"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block font-bold text-slate-700 uppercase font-mono mb-1">
                    Phone Number
                  </label>
                  <input
                    type="text"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="+91 86046 85533"
                    className="w-full px-3 py-2 rounded-xl bg-slate-50 border border-slate-200 text-slate-900 font-semibold focus:outline-hidden"
                  />
                </div>

                <div>
                  <label className="block font-bold text-slate-700 uppercase font-mono mb-1">
                    Job Title *
                  </label>
                  <input
                    type="text"
                    value={jobTitle}
                    onChange={(e) => setJobTitle(e.target.value)}
                    placeholder="e.g. Senior Theological Translator"
                    className="w-full px-3 py-2 rounded-xl bg-slate-50 border border-slate-200 text-slate-900 font-semibold focus:outline-hidden"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block font-bold text-slate-700 uppercase font-mono mb-1">
                    Department
                  </label>
                  <select
                    value={department}
                    onChange={(e) => setDepartment(e.target.value as StaffDepartment)}
                    className="w-full px-3 py-2 rounded-xl bg-slate-50 border border-slate-200 text-slate-900 font-semibold focus:outline-hidden"
                  >
                    {Object.entries(STAFF_DEPARTMENTS_CONFIG).map(([key, config]) => (
                      <option key={key} value={key}>{config.label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block font-bold text-slate-700 uppercase font-mono mb-1">
                    System Role
                  </label>
                  <select
                    value={role}
                    onChange={(e) => setRole(e.target.value as UserRole)}
                    className="w-full px-3 py-2 rounded-xl bg-slate-50 border border-slate-200 text-slate-900 font-semibold focus:outline-hidden"
                  >
                    <option value="SUPER_ADMIN">Super Admin (Full Governance)</option>
                    <option value="ADMIN">Admin (Operations &amp; CRM)</option>
                    <option value="EDITOR">Editor (Translations &amp; Proofs)</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block font-bold text-slate-700 uppercase font-mono mb-1">
                  Assigned Languages / Vernaculars
                </label>
                <div className="flex flex-wrap gap-1.5 p-2 bg-slate-50 rounded-xl border border-slate-200 max-h-24 overflow-y-auto">
                  {INDIAN_LANGUAGES_LIST.map((lang) => {
                    const isSelected = assignedLanguages.includes(lang);
                    return (
                      <button
                        key={lang}
                        type="button"
                        onClick={() => toggleLanguage(lang)}
                        className={`px-2.5 py-1 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                          isSelected
                            ? 'bg-slate-900 text-white shadow-xs'
                            : 'bg-white border border-slate-200 text-slate-700 hover:bg-slate-100'
                        }`}
                      >
                        {lang}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <label className="block font-bold text-slate-700 uppercase font-mono mb-1">
                  Professional Bio &amp; Role Context
                </label>
                <textarea
                  rows={2}
                  value={bio}
                  onChange={(e) => setBio(e.target.value)}
                  placeholder="Key responsibilities and qualifications..."
                  className="w-full px-3 py-2 rounded-xl bg-slate-50 border border-slate-200 text-slate-900 font-semibold focus:outline-hidden"
                />
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="isActive"
                  checked={isActive}
                  onChange={(e) => setIsActive(e.target.checked)}
                  className="rounded border-slate-300 text-brand-600 focus:ring-brand-500"
                />
                <label htmlFor="isActive" className="text-xs font-bold text-slate-800">
                  Account is Active and authorized for production access
                </label>
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t border-slate-100">
              <button
                onClick={() => setIsModalOpen(false)}
                className="px-4 py-2 rounded-xl text-slate-600 hover:bg-slate-100 font-bold cursor-pointer"
              >
                Cancel
              </button>
              <button
                onClick={() => saveMutation.mutate()}
                disabled={!name || !email || saveMutation.isPending}
                className="px-5 py-2 rounded-xl bg-slate-900 hover:bg-slate-800 text-white font-bold text-xs disabled:opacity-50 cursor-pointer shadow-sm"
              >
                {saveMutation.isPending ? 'Saving...' : editingStaff ? 'Save Changes' : 'Create Staff Member'}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};
